
import { useState } from 'react';
import { supabase } from "@/integrations/supabase/client";
import { getErrorMessage } from './authErrorHandler';

export const usePasswordResetHandler = () => {
  const [loading, setLoading] = useState(false);

  // Password reset functionality with improved error handling
  const handlePasswordReset = async (
    email: string
  ): Promise<{ success: boolean, error?: string }> => {
    if (!email) {
      return { 
        success: false, 
        error: "Please enter your email address" 
      };
    }
    
    setLoading(true);
    
    try {
      // Get the current origin for the redirect URL - use our dedicated reset page
      const redirectTo = `${window.location.origin}/auth/reset-password`;
      
      // Call Supabase to send password reset email
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo,
      });
      
      if (error) throw error;
      
      setLoading(false);
      return { success: true };
    } catch (error: any) {
      console.error("Password reset error:", error);
      setLoading(false);
      return { 
        success: false, 
        error: getErrorMessage(error)
      };
    }
  };

  // Function to update password after reset
  const handleUpdatePassword = async (
    password: string,
    confirmPassword: string
  ): Promise<{ success: boolean, error?: string }> => {
    if (!password || !confirmPassword) {
      return { 
        success: false, 
        error: "Please enter and confirm your new password" 
      };
    }
    
    if (password.length < 6) {
      return { 
        success: false, 
        error: "Password must be at least 6 characters long" 
      };
    }
    
    if (password !== confirmPassword) {
      return { 
        success: false, 
        error: "Passwords don't match" 
      };
    }
    
    setLoading(true);
    
    try {
      // Verify we have a valid session before attempting update
      const { data: sessionData, error: sessionError } = await supabase.auth.getSession();
      if (sessionError) {
        console.error("Session validation failed:", sessionError);
        throw new Error("Auth required. Try again.");
      }
      
      if (!sessionData.session) {
        console.error("No active session found for password update");
        throw new Error("Auth required. Try again.");
      }
      
      // Update the user's password with Supabase
      const { error } = await supabase.auth.updateUser({
        password: password
      });
      
      if (error) throw error;
      
      setLoading(false);
      return { success: true };
    } catch (error: any) {
      console.error("Update password error:", error);
      setLoading(false);
      return { 
        success: false, 
        error: getErrorMessage(error)
      };
    }
  };

  return {
    loading,
    handlePasswordReset,
    handleUpdatePassword
  };
};
